import { useEffect, useState } from "react";
import { Trophy, Medal, Loader2, Flame } from "lucide-react";

/**
 * DriverLeaderboardPanel — this-week's top earners. Injected into the driver
 * dashboard so friendly competition drives more accepted rides.
 *
 * Currently-hot drivers (3+ rides in the last 60 min) are lifted out into a
 * pinned "on a streak" ribbon at the top so dispatchers see who's firing
 * without scanning the whole table.
 */
export default function DriverLeaderboardPanel({ apiClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get("/driver/leaderboard");
        if (alive) setData(data);
      } catch {
        if (alive) setData(null);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-5 gold-border flex items-center gap-2 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading leaderboard…
      </div>
    );
  }
  const rows = data?.rows || [];
  const myRank = data?.my_rank;
  const streakers = rows.filter((r) => r.on_streak);

  return (
    <div className="glass-card rounded-2xl p-5 gold-border" data-testid="driver-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-400/30 grid place-items-center">
            <Trophy className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <div className="text-caption">This week</div>
            <h3 className="font-display font-bold">Top earners</h3>
          </div>
        </div>
        {myRank && (
          <div className="text-xs font-mono text-red-300" data-testid="leaderboard-my-rank">
            You're <b>#{myRank}</b> 🔥
          </div>
        )}
      </div>

      {/* Streak ribbon — only rendered while at least one driver is on fire.
          The horizontal scroll keeps it tidy even if 6 drivers are streaking. */}
      {streakers.length > 0 && (
        <div
          data-testid="streak-ribbon"
          className="mb-3 rounded-lg border border-orange-400/40 bg-gradient-to-r from-orange-500/15 via-red-500/10 to-amber-500/15 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-orange-400/20">
            <Flame className="w-3.5 h-3.5 text-orange-400" fill="currentColor" />
            <div className="text-[10px] uppercase tracking-widest font-mono text-orange-300">
              On a streak now · {streakers.length} driver{streakers.length !== 1 ? "s" : ""} firing
            </div>
          </div>
          <div className="flex gap-2 px-3 py-2 overflow-x-auto no-scrollbar">
            {streakers.map((s) => (
              <div
                key={s.driver_id}
                data-testid={`streak-chip-${s.rank}`}
                className={`shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-full text-xs font-mono border ${
                  s.is_me
                    ? "bg-red-500/25 border-red-400/60 text-red-100 animate-pulse"
                    : "bg-black/30 border-orange-400/30 text-orange-200"
                }`}
              >
                <Flame className="w-3 h-3 text-orange-400" fill="currentColor" />
                <span className="font-display font-bold text-white">{s.name}</span>
                <span className="text-orange-300">×{s.streak_count}</span>
                <span className="text-slate-500">·</span>
                <span className="text-emerald-300">NZ${s.earnings.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-slate-500 text-sm text-center py-6" data-testid="leaderboard-empty">
          Board resets Monday. Be the first to score this week.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <div
              key={r.driver_id}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm ${
                r.is_me
                  ? "bg-red-500/15 border border-red-400/40"
                  : "bg-black/20 border border-white/5"
              }`}
              data-testid={`leaderboard-row-${r.rank}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <RankBadge rank={r.rank} />
                <div className="min-w-0">
                  <div className={`font-medium truncate flex items-center gap-1.5 ${r.is_me ? "text-red-200" : "text-white"}`}>
                    {r.name}
                    {r.on_streak && (
                      <span title={`On a streak · ${r.streak_count} rides in the last hour`} className="inline-flex items-center gap-0.5 text-orange-400 animate-pulse" data-testid={`streak-${r.rank}`}>
                        <Flame className="w-3.5 h-3.5" fill="currentColor" />
                        <span className="text-[10px] font-mono">×{r.streak_count}</span>
                      </span>
                    )}
                    {r.is_me && <span className="text-red-300 ml-1">· you</span>}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">{r.vehicle}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-emerald-300">NZ$ {r.earnings.toFixed(2)}</div>
                <div className="text-[10px] text-slate-500 font-mono">{r.rides} rides · NZ$ {r.tips.toFixed(2)} tips</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

const RankBadge = ({ rank }) => {
  if (rank === 1) return <Medal className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="w-5 text-center text-slate-500 font-mono text-xs">#{rank}</span>;
};
